import { MAX_MEALS_PER_SLOT, type PlannedMeal } from '../../../redux/mealPlanSlice';
import { isEntryMeal, isExclusiveEntryMeal } from '../../../redux/mealEntry';
import { slotKey, dowToDi } from './constants';

/**
 * Slot grouping for the v2 planner.
 *
 * A slot (one day × one meal type) can legitimately hold up to
 * MAX_MEALS_PER_SLOT recipes — a main plus a side, for instance. The planner
 * used to key its meals in a `Map<number, PlannedMeal>`, so every extra dish in
 * a slot silently overwrote the previous one: the meal stayed in the plan (and
 * on the grocery list) but was never drawn. These helpers keep the whole stack.
 *
 * Proposals (mode: 'proposed') are competing recipes awaiting household approval.
 * They have a separate MAX_PROPOSALS_PER_SLOT budget and cannot consume the
 * 3-dish (accepted meal) budget. The primary slot card is always an accepted meal,
 * never a proposal.
 */

/** A meal is accepted unless explicitly marked as proposed. */
export function isAccepted(meal: PlannedMeal): boolean {
  return meal.mode !== 'proposed';
}

/** A meal is a proposal competing for the slot. */
export function isProposal(meal: PlannedMeal): boolean {
  return meal.mode === 'proposed';
}

export const MI_BY_MEAL_TYPE: Record<string, number> = {
  breakfast: 0,
  lunch: 1,
  dinner: 2,
};

/** slotKey (mi * 7 + di) for a planned meal, Monday-first. */
export function slotKeyOfMeal(meal: PlannedMeal): number {
  const di = dowToDi(meal.dayOfWeek ?? 0);
  const mi = MI_BY_MEAL_TYPE[meal.mealType] ?? 1;
  return slotKey(mi, di);
}

/**
 * Group the week's meals by slot, keeping every meal.
 * Within each slot, accepted meals come first (sorted by createdAt), then proposals (sorted by createdAt).
 * This ensures the primary card is always an accepted meal, and proposals are rendered as extras below it.
 */
export function buildSlotMeals(weekMeals: PlannedMeal[]): Map<number, PlannedMeal[]> {
  const map = new Map<number, PlannedMeal[]>();
  weekMeals.forEach((meal) => {
    const sk = slotKeyOfMeal(meal);
    const bucket = map.get(sk);
    if (bucket) bucket.push(meal);
    else map.set(sk, [meal]);
  });
  map.forEach((meals) => {
    if (meals.length > 1) meals.sort(byAcceptanceThenCreatedAt);
  });
  return map;
}

/**
 * Sort meals by status first (accepted before proposals), then by creation time.
 * This keeps the primary card stable and ensures it's always accepted.
 */
function byAcceptanceThenCreatedAt(a: PlannedMeal, b: PlannedMeal): number {
  // Accepted meals come first
  const aAccepted = isAccepted(a);
  const bAccepted = isAccepted(b);
  if (aAccepted !== bAccepted) {
    return aAccepted ? -1 : 1;
  }
  // A note or leftovers are the slot's main: the sides added next to them
  // draw under them whatever order they were written in.
  const aEntry = isEntryMeal(a);
  if (aEntry !== isEntryMeal(b)) return aEntry ? -1 : 1;
  // Within the same status, sort by creation time
  return byCreatedAt(a, b);
}

/** Stable ascending sort on createdAt; meals without one keep their order, last. */
function byCreatedAt(a: PlannedMeal, b: PlannedMeal): number {
  if (!a.createdAt && !b.createdAt) return 0;
  if (!a.createdAt) return 1;
  if (!b.createdAt) return -1;
  return a.createdAt < b.createdAt ? -1 : a.createdAt > b.createdAt ? 1 : 0;
}

/**
 * The primary (first accepted) meal of each slot — for the consumers that still work
 * one meal at a time: move/swap, leftovers, drag-and-drop.
 * A slot holding only proposals has no primary (the card is absent).
 */
export function primarySlotMap(slotMeals: Map<number, PlannedMeal[]>): Map<number, PlannedMeal> {
  const map = new Map<number, PlannedMeal>();
  slotMeals.forEach((meals, sk) => {
    // The primary is the slot's main (`slotMainOf`).
    // A slot of only proposals has no primary.
    const primary = slotMainOf(meals);
    if (primary) map.set(sk, primary);
  });
  return map;
}

/**
 * The main of a slot: an entry when there is one (a note or leftovers with
 * their sides), otherwise the first accepted meal. Null for a slot of
 * proposals only.
 */
export function slotMainOf(meals: PlannedMeal[] | undefined): PlannedMeal | null {
  if (!meals) return null;
  return meals.find((m) => isAccepted(m) && isEntryMeal(m)) ?? meals.find(isAccepted) ?? null;
}

/**
 * Split a slot's dishes into the primary card and the rows stacked under it.
 *
 * The primary is **always an accepted meal** — the same rule primarySlotMap
 * enforces for desktop. Mobile used to take `dishes[0]` straight off the bucket,
 * so a slot holding only proposals drew a proposal as a full MealRow:
 * indistinguishable from a decision, with no "put on the menu" button and no way
 * to tell whose idea it was.
 *
 * Proposals stay in `extras` — they are still drawn, as the dashed proposal
 * cards, under an accepted meal. Dropping them here instead would have fixed the
 * masquerade by deleting the case that already worked.
 *
 * Generic over the caller's row shape so DayList can keep its {meal, recipe}
 * pairs; buildSlotMeals has already sorted accepted-first then by createdAt, so
 * filtering twice preserves the order on screen.
 */
export function splitSlotDishes<T extends { meal: PlannedMeal }>(
  dishes: T[]
): { primary: T | null; extras: T[] } {
  const accepted = dishes.filter((d) => isAccepted(d.meal));
  const proposals = dishes.filter((d) => isProposal(d.meal));
  const [primary, ...rest] = accepted;
  return { primary: primary ?? null, extras: [...rest, ...proposals] };
}

/**
 * Can another dish be stacked in this slot? Resto and "rien" hold exactly one
 * thing by design; a note or leftovers take sides like a recipe main. A slot
 * caps at MAX_MEALS_PER_SLOT dishes, the entry counting as one. Proposals have
 * their own budget and don't count against this limit.
 */
export function canAddDish(meals: PlannedMeal[] | undefined): boolean {
  if (!meals || meals.length === 0) return true;
  if (meals.some(isExclusiveEntryMeal)) return false;
  // Count only accepted meals against the budget; proposals are a separate concern
  const acceptedCount = meals.filter(isAccepted).length;
  return acceptedCount < MAX_MEALS_PER_SLOT;
}
